/**
 * TA 的小动作（/me/moves，TEACH-PAGES-117）：从「我的」进来的全屏页，左上角回 /me，写法照 /me/look（WorldGate 守）。
 * - 视觉照 r7k 参考稿 01-list-A（TEACH-PAGES-VISUAL-117，I 10-16 选 A 方向；结构不推翻）：
 *   顶上是 TA 在自己小窝里的场景（./TeachHero），下面一组一张大卡，底部吸底一行额度加主按钮（列表再长也一直按得到）。
 *   主按钮照全站一套（暖可可 --c-deep-ink，ui.css 与 ui-base-01a：features 不许给主按钮改底色），不照抄参考稿的橙（I 10-16：用全站暖色令牌）。
 *   每行不放小图（没有真实的动作缩略图，不拿同一张头像凑），列表上不放开关（启用、停用在那一单的页面里，先看过再决定）。I 10-16 定。
 * - 额度（方案 9.1：放在教学入口附近，按账号写）在主按钮上面那一行，整句照服务端给的数写。
 * - 一个主要操作：「教 TA 一个新动作」；次数用完时换成「先记下想教 TA 的动作」（草稿照样能存，不出购买按钮，9.3）。
 * - 下面按组列：等你确认（唯一要马上动手的，放最前）、已学会、准备中、还没写完、这几次没准备好（I 10-16：基础动作读口没定之前不画那一组，不凑假数据）。
 * - 基础动作（UI2-BASIC-MOTION-ROWS-117，A-2 BASIC-PACK-117 的读口）：「已学会」后面一张卡（./TeachBasicMotions），
 *   「准备中」那一组里多一行「TA 还在学 N 个基础动作」，点了跳到卡上；读口没有这一栏就整块不出。
 *   行上的标签和组名一样时不重复（设计评审第一轮中 17）。
 * - 读不到就如实说：演示模式、没开放、没有照顾权限、读失败各说各的；不编记录。
 */
import { useState } from "react";
import { Link } from "react-router";
import { ErrorState, Icon, LoadingState, Page, TopBar, type IconName } from "@/shared/ui";
import { WorldGate } from "@/features/world_map/WorldGate";
import { useCurrentPet, type CurrentPet } from "@/features/memories/currentPet";
import { GROUP_TITLE, TEACH_ACTION, TEACH_ENTRY_TITLE, TEACH_INVITE, TEACH_SAVE_IDEA, canTeachNew, draftExcerpt, entitlementLine, groupOrders, headerTitle, rowTag, stateCopy } from "./teachModel";
import type { TeachOrder } from "./teachTypes";
import { useBasicMotions, useCanCare, useOrdersOfPet, useTeachApi, useTeachOpen } from "./useTeach";
import { learningCount } from "./basicMotions";
import { BasicLearningRow, TeachBasicMotions, firstLearningCategory, type BasicJump } from "./TeachBasicMotions";
import { TeachHero } from "./TeachHero";
import "@/features/me/me.css";
import "./teach.css";

export const TEACH_DEMO = "这里暂时看不了 TA 的小动作。";
export const TEACH_CLOSED = "教 TA 新动作还没有开放。";
export const TEACH_NOT_CARER = "照顾 TA 的家人才能教 TA 新动作。";
export const TEACH_NO_PET = "还没有住进来的伙伴。";

export function TeachHomePage() {
  return (
    <WorldGate>
      <Page bare className="ps-teach">
        <TopBar title={TEACH_ENTRY_TITLE} back="/me" backLabel="返回我的" />
        <TeachHomeBody />
      </Page>
    </WorldGate>
  );
}

function TeachHomeBody() {
  const api = useTeachApi();
  const open = useTeachOpen();
  const canCare = useCanCare();
  const current = useCurrentPet();
  if (!api) return <p className="ps-teach-quiet" role="status">{TEACH_DEMO}</p>;
  if (current.status === "pending" || open === null || canCare === null) return <LoadingState lines={2} label="正在找到 TA…" />;
  if (current.status === "error") return <ErrorState error={current.error} onRetry={current.retry} />;
  if (!current.pet) return <p className="ps-teach-quiet" role="status">{TEACH_NO_PET}</p>;
  if (!open) return <p className="ps-teach-quiet" role="status">{TEACH_CLOSED}</p>;
  if (!canCare) return <p className="ps-teach-quiet" role="status">{TEACH_NOT_CARER}</p>;
  return <TeachHomeForPet key={current.pet.petId} pet={current.pet} />;
}

function TeachHomeForPet({ pet }: { pet: CurrentPet }) {
  const orders = useOrdersOfPet(pet.petId);
  const basic = useBasicMotions(pet.petId);
  const [jump, setJump] = useState<BasicJump | null>(null);
  if (orders.isPending) return <LoadingState lines={3} label="正在看看 TA 学过什么…" />;
  if (orders.isError) return <ErrorState error={orders.error} onRetry={() => void orders.refetch()} />;
  const { items, entitlement } = orders.data;
  const groups = groupOrders(items);
  const canTeach = canTeachNew(entitlement);
  const learning = basic ? learningCount(basic) : 0;
  const toBasic = () => {
    const category = basic ? firstLearningCategory(basic) : null;
    if (category) setJump((last) => ({ category, seq: (last?.seq ?? 0) + 1 }));
  };
  // 基础动作那张卡接在「已学会」后面（等你确认、已学会在它前面，其余在它后面）
  const before = groups.filter(({ group }) => group === "confirm" || group === "learned");
  const after = groups.filter(({ group }) => group !== "confirm" && group !== "learned");
  const renderGroup = ({ group, orders: list }: (typeof groups)[number]) => (
    <section key={group} className="ps-teach-card ps-teach-group" aria-labelledby={`teach-group-${group}`} data-teach-group={group}>
      <h3 id={`teach-group-${group}`} className="ps-teach-group__title">
        <Icon name={GROUP_ICON[group]} size={18} className="ps-teach-group__icon" />
        <span>{GROUP_TITLE[group]}</span>
      </h3>
      <ul className="ps-teach-list">
        {list.map((order) => <TeachOrderRow key={order.order_id} order={order} />)}
        {group === "in_progress" && learning > 0 ? <BasicLearningRow count={learning} onJump={toBasic} /> : null}
      </ul>
    </section>
  );
  return (
    <>
      <TeachHero pet={pet} orders={items} />

      {groups.length === 0 ? <div className="ps-teach-card ps-teach-invite"><p className="ps-teach-lead">{TEACH_INVITE}</p></div> : null}

      {before.map(renderGroup)}
      {basic ? <TeachBasicMotions motions={basic} jump={jump} /> : null}
      {after.map(renderGroup)}

      {/* 吸底：额度一行加主按钮（参考稿 01-list-A 的底部；安全区留白，列表再长也一直按得到） */}
      <div className="ps-teach-dock">
        <p className="ps-teach-dock__quota" data-teach-quota>{entitlementLine(entitlement)}</p>
        <Link to="/me/moves/new" className={`ps-btn ps-btn--block ${canTeach ? "ps-btn--primary" : "ps-btn--secondary"}`} data-teach-new>
          <Icon name={canTeach ? "sparkle" : "bookmark"} size={18} />
          {canTeach ? TEACH_ACTION : TEACH_SAVE_IDEA}
        </Link>
      </div>
    </>
  );
}

/** 组标题前的小图标（参考稿的叶子、芽）：只是装饰，读屏只念组名 */
const GROUP_ICON = { confirm: "check", learned: "heart", in_progress: "sprout", unfinished: "diary", ended: "info" } as const satisfies Record<keyof typeof GROUP_TITLE, IconName>;

function TeachOrderRow({ order }: { order: TeachOrder }) {
  const copy = stateCopy(order.state);
  if (!copy) return null;
  const tag = rowTag(order.state);
  return (
    <li>
      <Link to={`/me/moves/${encodeURIComponent(order.order_id)}`} className="ps-teach-row" data-state={order.state}>
        <span className="ps-teach-row__text">
          {/* 名字和一单页面顶上一样；草稿那一行的小字放描述的头几个字，好认（6c2b 轻审挑刺） */}
          <span className="ps-teach-row__title">{headerTitle(order)}</span>
          <span className="ps-teach-row__line">{(order.state === "draft" ? draftExcerpt(order) : null) ?? copy.line}</span>
        </span>
        {tag ? <span className="ps-teach-row__tag">{tag}</span> : null}
        <Icon name="chevron" size={16} className="ps-teach-row__go" />
      </Link>
    </li>
  );
}
